
import { t } from '@/i18n'
import Vue from 'vue'
import Vuex from 'vuex'
// import router from '@/router'
import app from './modules/app'
import settings from './modules/settings'
import user from './modules/user'
import api from '@/utils/api'
import { Message, MessageBox } from 'element-ui'
import { parseTime } from '@/utils/index'
import { normalizeSiteRuntime } from '@/utils/siteRuntime'
import { getEventThemeColor } from '@/utils/theme'
import { openAnnouncementNotification } from '@/utils/uiNotification'
import { getOrderArchiveNoticeDate } from '@/utils/orderArchiveNotice'
import { withTerminalDataSync } from '@/utils/terminalDataSync'

// 把vuex作为插件引入到Vue示例中 ps:注册
Vue.use(Vuex)

/** 创建一架飞机首次进入终端时的实时状态容器。 */
function createPlaneState () {
  return {
    isUnlock: null,
    heartBeat: null,
    heartRandom: null,
    heartbeatSamples: [],
    heartbeatObservedAt: 0,
    heartbeatHealthUpdatedAt: 0,
    online: false,
    onlineTimeout: null,
    lastFlightHeartbeatAt: 0,
    flightOfflineSince: 0,
    lastTelemetryAt: 0,
    espOnline: false,
    espLastSeenAt: 0,
    espRandom: null,
    espOnlineTimeout: null,
    voltagBattery: null,
    currentBattery: null,
    batteryRemaining: null,
    positionAlt: null,
    laserAlt: null,
    groundSpeed: null,
    satCount: null,
    fixType: null,
    completionPct: 0,
    reportCal: null,
    questState: 1,
    missionCurrentSeq: -1,
    missionToken: '',
    orphanResetPhase: 'waiting',
    missionReady: false,
    acceState: null,
    getPlaneMode: null,
    loadweight: null,
    AlignStatus: 0,
    hasGoods: null,
    hookstatus: null,
    parabolaServo: null,
    isAutoGoodsDown: null,
    isDescentActive: false,
    oscStatus: false,
    oscStatusTick: 0,
    position: [],
    battCapacity: null,
    homePosition: null,
    wpnavSpeed: null,
    parameterValue: null,
    heading: null,
    deviceSwitchState: {
      buzzerEnable: null
    }
  }
}

function clearPlaneStateTimers (planeState) {
  if (!planeState) return
  if (planeState.onlineTimeout) clearTimeout(planeState.onlineTimeout)
  if (planeState.espOnlineTimeout) clearTimeout(planeState.espOnlineTimeout)
}

/** 兼容空值、旧单值和损坏JSON，避免一条脏数据中断整批基础数据刷新。 */
function parseJsonArray (value, wrapScalar = false) {
  if (Array.isArray(value)) return value
  if (value === null || value === undefined || value === '') return []
  try {
    const parsed = JSON.parse(value)
    if (Array.isArray(parsed)) return parsed
    return wrapScalar && parsed ? [parsed] : []
  } catch (error) {
    return wrapScalar ? [value] : []
  }
}

function normalizeTileZoomRanges (ranges) {
  const normalized = {}
  const supportedKeys = ['global', 'china', 'shopWide', 'shopDetail']
  supportedKeys.forEach(key => {
    const value = ranges && Array.isArray(ranges[key]) ? ranges[key] : null
    if (!value || value.length < 2) return
    const minimum = Math.max(0, Math.min(22, Number(value[0])))
    const maximum = Math.max(minimum, Math.min(22, Number(value[1])))
    if (Number.isFinite(minimum) && Number.isFinite(maximum)) normalized[key] = [minimum, maximum]
  })
  return normalized
}

// Only the newest response in the current login session may update shared data.
const taskRefreshVersions = new Map()
function beginTaskRefresh (key, state) {
  const version = (taskRefreshVersions.get(key) || 0) + 1
  const token = state.user.token
  taskRefreshVersions.set(key, version)
  return () => taskRefreshVersions.get(key) === version && state.user.token === token
}

const store = new Vuex.Store(withTerminalDataSync({
  state: {
    shopList: [], // 商铺列表
    adminList: [], // 管理员列表
    roleList: [], // 当前商铺角色列表
    permissionList: [], // 当前账号可分配的权限目录
    airList: [], // 所有飞机列表
    planeClassList: [], // 机型列表
    planeModelList: [], // 飞机模块列表
    siteList: [], // 站点列表
    routeList: [], // 航线列表
    noflyData: [[], [], []], // [0]禁飞区数据 [1]限制飞区 [2]限飞区高度
    categoryList: [], // 分类列表
    bannerList: [], // 轮播图列表
    noticeList: [], // 滚动通知列表
    spuList: [], // 商品spu列表
    skuList: [], // 商品sku列表
    paidOrderListLoaded: false, // 首次订单快照完成后才识别增量提醒
    paidOrderList: [], // 已付款 和已退款但是发货状态为 已发货 订单列表
    logList: [], // 操作日志列表
    messageList: [], // 管理员公告列表
    mapStyleList: [], // 地图样式列表
    mapRuntimeConfigLoaded: false, // 当前会话是否已读取地图代理运行参数
    mapTileUrl: '', // 地图代理公开瓦片地址
    tileZoomRanges: {}, // NAS 完整瓦片包提供的实际层级
    ADSBList: [], // 存放当前活跃的 ADSB 飞机数据
    voiceLearningCandidates: {} // 当前运行期间的语音误识别学习候选
  },
  mutations: {
    // 账号切换时清空会话业务数据，避免上一个账号的数据被下一账号读取
    resetSessionData (state) {
      taskRefreshVersions.forEach((version, key) => taskRefreshVersions.set(key, version + 1))
      state.shopList = []
      state.adminList = []
      state.roleList = []
      state.permissionList = []
      state.airList = []
      state.planeClassList = []
      state.planeModelList = []
      state.siteList = []
      state.routeList = []
      state.noflyData = [[], [], []]
      state.categoryList = []
      state.bannerList = []
      state.noticeList = []
      state.spuList = []
      state.skuList = []
      state.paidOrderList = []
      state.paidOrderListLoaded = false
      state.logList = []
      state.messageList = []
      state.mapStyleList = []
      state.mapRuntimeConfigLoaded = false
      state.mapTileUrl = ''
      state.tileZoomRanges = {}
      state.ADSBList = []
    },
    recordVoiceLearningCandidate (state, { text, key }) {
      if (!text || !key) return
      const oldItem = state.voiceLearningCandidates[text]
      const candidate = oldItem && oldItem.key === key
        ? { key, count: oldItem.count + 1, lastSeen: Date.now() }
        : { key, count: 1, lastSeen: Date.now() }
      Vue.set(state.voiceLearningCandidates, text, candidate)

      const entries = Object.entries(state.voiceLearningCandidates)
      if (entries.length > 50) {
        entries
          .sort((a, b) => Number(a[1].lastSeen || 0) - Number(b[1].lastSeen || 0))
          .slice(0, entries.length - 50)
          .forEach(item => Vue.delete(state.voiceLearningCandidates, item[0]))
      }
    },
    removeVoiceLearningCandidate (state, text) {
      Vue.delete(state.voiceLearningCandidates, text)
    },
    updatePlanePosition (state, { planeId, position }) {
      const plane = state.airList.find(p => p.id === planeId)
      if (plane) {
        if (!plane.planeState) {
          Vue.set(plane, 'planeState', {})
        }
        if (!plane.planeState.position) {
          Vue.set(plane.planeState, 'position', [])
        }

        // 追加新点
        plane.planeState.position.push(...position)
      }
    },
    /**
     * @description: 设置商铺列表
     */
    setShopList (state, list) {
      state.shopList = list
    },
    /**
     * @description: 设置管理员列表
     */
    setAdminList (state, list) {
      state.adminList = list
    },
    setRoleList (state, list) {
      state.roleList = list
    },
    setPermissionList (state, list) {
      state.permissionList = list
    },
    /**
     * @description: 设置飞机列表
     */
    setAirList (state, list) {
      state.airList = list
    },
    /**
     * @description:更新飞机的 parameterValue
     */
    updateParameterValue (state, { planeId, parameterValue }) {
      const plane = state.airList.find(plane => plane.id === planeId) // 根据 planeId 查找飞机
      if (plane) {
        plane.planeState.parameterValue = parameterValue // 更新 parameterValue
      }
    },
    /**
     * @description: 设置站点列表
     */
    setSiteList (state, list) {
      state.siteList = list
    },
    /**
     * @description: 设置航线列表
     */
    setRouteList (state, list) {
      state.routeList = list
    },
    /**
     * @description: 设置机型列表
     */
    setPlaneClassList (state, list) {
      state.planeClassList = list
    },
    /**
     * @description: 设置飞机模块列表
     */
    setPlaneModelList (state, list) {
      state.planeModelList = list
    },
    /**
     * @description: 设置指定飞机的设备开关状态集合
     */
    setPlaneDeviceSwitchState (state, { planeId, deviceSwitchState }) {
      const plane = state.airList.find(p => String(p.id) === String(planeId))

      if (!plane) {
        console.warn(`Plane with ID ${planeId} not found`)
        return
      }

      if (!plane.planeState) {
        Vue.set(plane, 'planeState', {})
      }

      if (!plane.planeState.deviceSwitchState) {
        Vue.set(plane.planeState, 'deviceSwitchState', {
          buzzerEnable: null
        })
      }

      const allowKeys = ['buzzerEnable']

      Object.keys(deviceSwitchState || {}).forEach(key => {
        if (allowKeys.includes(key)) {
          Vue.set(plane.planeState.deviceSwitchState, key, deviceSwitchState[key])
        }
      })
    },
    /**
     * @description: 设置禁飞区列表
     */
    setNoflyData (state, payload) {
      const list = Array.isArray(payload) ? payload : []
      const noflyPolygons = []
      const restrictedPolygons = []
      const restrictedHeights = []
      list.forEach(region => {
        const coordinates = Array.isArray(region.coordinates)
          ? region.coordinates
          : parseJsonArray(region.coordinates)
        if (!Array.isArray(coordinates) || coordinates.length < 3) return
        if (region.restriction_type === 'restricted') {
          restrictedPolygons.push(coordinates)
          restrictedHeights.push(Number(region.limit_height) || 0)
        } else if (region.restriction_type === 'nofly') {
          noflyPolygons.push(coordinates)
        }
      })
      const next = [noflyPolygons, restrictedPolygons, restrictedHeights]
      if (JSON.stringify(state.noflyData) !== JSON.stringify(next)) state.noflyData = next
    },
    /**
     * @description: 设置分类列表
     */
    setCategoryList (state, list) {
      state.categoryList = list
    },
    /**
     * @description: 设置轮播图列表
     */
    setBannerList (state, list) {
      state.bannerList = list
    },
    setNoticeList (state, list) {
      state.noticeList = list
    },
    /**
     * @description: 设置商品spu列表
     */
    setSpuList (state, list) {
      state.spuList = list
    },
    /**
     * @description: 设置商品sku列表
     */
    setSkuList (state, list) {
      state.skuList = list
    },
    /**
     * @description: 设置 已付款 和已退款但是发货状态为 已发货 订单列表
     */
    applyDeliveryTaskFinished (state, form) {
      const order = state.paidOrderList.find(item => String(item.id) === String(form.order_id) &&
        String(item.shop_id) === String(form.shop_id) && String(item.by_plane_id) === String(form.plane_id) &&
        item.shipment_status === '已发货' && item.mission_token === form.mission_token)
      if (!order) return
      const routeId = String(order.mission_route_id || '')
      // Fence pre-completion reads before applying the known successful write.
      ;['fetchPaidOrderList', 'fetchSiteList'].forEach(key => taskRefreshVersions.set(key, (taskRefreshVersions.get(key) || 0) + 1))
      order.shipment_status = form.result === 'delivered' ? '已送达' : '已接单'
      if (form.result !== 'delivered') order.by_plane_id = null
      order.mission_token = null
      order.mission_route_id = null
      state.siteList.forEach(site => {
        if (String(site.shop_id) !== String(form.shop_id)) return
        const runtime = normalizeSiteRuntime(site)
        runtime.runingArray = runtime.runingArray.map((planeId, index) =>
          planeId === String(form.plane_id) && runtime.bindRouteArray[index] === routeId ? '' : planeId)
        site.runing = JSON.stringify(runtime.runingArray)
        Vue.set(site, 'runing_array', runtime.runingArray)
      })
    },
    setPaidOrderList (state, list) {
      state.paidOrderList = list
      state.paidOrderListLoaded = true
    },
    /**
     * @description: 向操作日志列表最后插入新的信息
     * @param {*} logData
     */
    insertNewLog (state, logData) {
      state.logList.push(logData)
      // 实时日志只承担当前会话预览，限制缓存规模以避免长时间运行后筛选和渲染变慢。
      if (state.logList.length > 500) {
        state.logList.splice(0, state.logList.length - 500)
      }
    },
    /**
     * @description: 清空实时操作日志
     */
    clearLogList (state) {
      state.logList = []
    },
    /**
     * @description: 向管理员公告列表最后插入新的信息
     * @param {*} list
     */
    setMessageList (state, list) {
      state.messageList = list
    },
    /**
     * @description: 设置当前活跃的 ADSB 飞机数据
     * @param {*} ADSB
     */
    setADSBList (state, ADSB) {
      const index = state.ADSBList.findIndex(i => i.icao === ADSB.icao)
      if (index > -1) {
        // 更新已有
        state.ADSBList[index] = { ...ADSB, timestamp: Date.now() }
      } else {
        // 新增
        state.ADSBList.push({ ...ADSB, timestamp: Date.now() })
      }
    },
    /** 设置地图样式列表。 */
    setMapStyleList (state, list) {
      state.mapStyleList = Array.isArray(list) ? list : []
    },
    setMapRuntimeConfig (state, config) {
      state.mapTileUrl = config && typeof config.tileUrl === 'string' ? config.tileUrl.trim() : ''
      state.tileZoomRanges = normalizeTileZoomRanges(config && config.tileZoomRanges)
      state.mapRuntimeConfigLoaded = state.mapTileUrl !== '' && Object.keys(state.tileZoomRanges).length > 0
    },
    /**
     *  @description: 清除过期的 ADSB 数据
    */
    cleanExpiredADSBList (state) {
      const now = Date.now()
      state.ADSBList = state.ADSBList.filter(i => now - i.timestamp < 20000)
    }
  },
  actions: {
    async fetchMapRuntimeConfig ({ commit }) {
      const res = await api.get('getMapRuntimeConfig', 'Plane')
      commit('setMapRuntimeConfig', res.data.status === 1 ? res.data : null)
      return res.data.status === 1
    },
    async fetchPermissionCatalog ({ commit }) {
      const res = await api.get('getPermissionCatalog', 'Admin')
      commit('setPermissionList', res.data.status === 1 ? (res.data.permissionList || []) : [])
      return res
    },
    async fetchRoleList ({ commit }, shopId) {
      const params = new URLSearchParams()
      const resolvedShopId = store.state.user.is_god === true
        ? (shopId || '')
        : (store.state.user.shop_id || '')
      params.append('shop_id', resolvedShopId)
      const res = await api.post('getRoleList', params, 'Admin')
      commit('setRoleList', res.data.status === 1 ? (res.data.roleList || []) : [])
      return res
    },
    async fetchAddRole ({ dispatch }, form) {
      const params = new URLSearchParams()
      params.append('shop_id', form.shop_id || store.state.user.shop_id || '')
      params.append('name', form.name)
      params.append('permissions', JSON.stringify(form.permissions || []))
      const res = await api.post('addRole', params, 'Admin')
      if (res.data.status === 1) {
        Message.success(t(res.data.msg))
        await dispatch('fetchRoleList', form.shop_id)
      } else Message.error(t(res.data.msg))
      return res
    },
    async fetchSaveRole ({ dispatch }, form) {
      const params = new URLSearchParams()
      params.append('id', form.id)
      params.append('shop_id', form.shop_id || store.state.user.shop_id || '')
      params.append('name', form.name)
      params.append('permissions', JSON.stringify(form.permissions || []))
      const res = await api.post('saveRole', params, 'Admin')
      if (res.data.status === 1) {
        Message.success(t(res.data.msg))
        await dispatch('fetchRoleList', form.shop_id)
      } else Message.error(t(res.data.msg))
      return res
    },
    async fetchDeleteRole ({ dispatch }, form) {
      const params = new URLSearchParams()
      params.append('id', form.id)
      params.append('shop_id', form.shop_id || store.state.user.shop_id || '')
      const res = await api.post('deleteRole', params, 'Admin')
      if (res.data.status === 1) {
        Message.success(t(res.data.msg))
        await dispatch('fetchRoleList', form.shop_id)
        dispatch('fetchAdminList')
      } else Message.error(t(res.data.msg))
      return res
    },
    /**
     * @description: 获取商铺列表
     */
    async fetchShopList ({ commit }) {
      const res = await api.get('getShopList', 'Admin')
      if (!res.data || !Array.isArray(res.data.shopList) || ![0, 1].includes(Number(res.data.status)) || (Number(res.data.status) === 0 && res.data.shopList.length > 0)) {
        throw new Error((res.data && res.data.msg) || '数据读取失败')
      }
      if (res.data.status === 1) {
        res.data.shopList.forEach((shop, index) => {
          shop.remark_presup = parseJsonArray(shop.remark_presup)
          shop.refund_remark_presup = parseJsonArray(shop.refund_remark_presup)
          shop.cancel_remark_presup = parseJsonArray(shop.cancel_remark_presup)
          shop.voice_presup = parseJsonArray(shop.voice_presup)
          shop.logo = parseJsonArray(shop.logo, true)
          shop.logo.forEach((logo, i) => {
            res.data.shopList[index].logo[i] = settings.state.logoPath + logo // 把绝对路径加上
          })
        })
        commit('setShopList', res.data.shopList)
      } else {
        commit('setShopList', [])
      }
      return res
    },
    /**
     * @description: 添加商铺
     * @param {*} 商铺信息表单
     * @return {*} 服务器返回值
     */
    async fetchAddShop ({ dispatch }, form) {
      const params = new URLSearchParams()
      params.append('admin_name', form.admin_name)
      params.append('pwd', form.pwd)
      params.append('name', form.name)
      params.append('waiter', form.waiter)
      params.append('service_wx', form.service_wx)
      params.append('tel', form.tel)
      params.append('email', form.email)
      params.append('price_min', form.price_min)
      params.append('weight_max', form.weight_max)
      params.append('site_cache_minutes', form.site_cache_minutes || 10)
      params.append('checkout_site_check_enabled', form.checkout_site_check_enabled === 0 || form.checkout_site_check_enabled === '0' ? 0 : 1)
      params.append('delivery_buffer_minutes', form.delivery_buffer_minutes || 3)
      params.append('default_transport_price', form.default_transport_price)
      params.append('default_pack_price', form.default_pack_price)
      params.append('food_sn_mode', form.food_sn_mode)
      params.append('food_sn_start_min', form.food_sn_start_min)
      params.append('food_sn_start_max', form.food_sn_start_max)
      params.append('food_sn_step_min', form.food_sn_step_min)
      params.append('food_sn_step_max', form.food_sn_step_max)
      params.append('food_sn_random_min', form.food_sn_random_min)
      params.append('food_sn_random_max', form.food_sn_random_max)
      params.append('remark_presup', JSON.stringify(form.remark_presup))
      params.append('refund_remark_presup', JSON.stringify(form.refund_remark_presup))
      params.append('cancel_remark_presup', JSON.stringify(form.cancel_remark_presup))
      params.append('voice_presup', JSON.stringify(form.voice_presup || []))
      params.append('desc', form.desc)
      params.append('manual_closed', form.manual_closed || '0')
      params.append('manual_closed_desc', form.manual_closed_desc || '')
      params.append('upFile', form.upFile)
      params.append('opening_time', form.opening_time)
      params.append('closeing_time', form.closeing_time)
      params.append('latitude', form.latitude)
      params.append('longitude', form.longitude)
      params.append('permissions', JSON.stringify(form.permissions || []))
      const res = await api.post('addShop', params, 'God')
      if (res.data.status === 1) {
        await dispatch('fetchShopList')// 刷新商铺列表
        await dispatch('fetchAdminList')// 刷新用户列表
        Message.success(t(res.data.msg))
      } else {
        Message.error(t(res.data.msg))
      }
      return res
    },
    /**
     * @description: 更新管商铺信息
     * @param {*} 商铺信息表单
     * @return {*} 服务器返回值
     */
    async fetchSaveShop ({ dispatch }, form) {
      const params = new URLSearchParams()
      params.append('shop_id', form.shop_id)
      params.append('name', form.name)
      params.append('waiter', form.waiter)
      params.append('service_wx', form.service_wx)
      params.append('tel', form.tel)
      params.append('email', form.email)
      params.append('price_min', form.price_min)
      params.append('weight_max', form.weight_max)
      params.append('site_cache_minutes', form.site_cache_minutes || 10)
      params.append('checkout_site_check_enabled', form.checkout_site_check_enabled === 0 || form.checkout_site_check_enabled === '0' ? 0 : 1)
      params.append('delivery_buffer_minutes', form.delivery_buffer_minutes || 3)
      params.append('default_transport_price', form.default_transport_price)
      params.append('default_pack_price', form.default_pack_price)
      params.append('food_sn_mode', form.food_sn_mode)
      params.append('food_sn_start_min', form.food_sn_start_min)
      params.append('food_sn_start_max', form.food_sn_start_max)
      params.append('food_sn_step_min', form.food_sn_step_min)
      params.append('food_sn_step_max', form.food_sn_step_max)
      params.append('food_sn_random_min', form.food_sn_random_min)
      params.append('food_sn_random_max', form.food_sn_random_max)
      params.append('remark_presup', JSON.stringify(form.remark_presup))
      params.append('refund_remark_presup', JSON.stringify(form.refund_remark_presup))
      params.append('cancel_remark_presup', JSON.stringify(form.cancel_remark_presup))
      params.append('voice_presup', JSON.stringify(form.voice_presup || []))
      params.append('desc', form.desc)
      params.append('manual_closed', form.manual_closed || '0')
      params.append('manual_closed_desc', form.manual_closed_desc || '')
      params.append('upFile', form.upFile)
      params.append('oldFile', form.oldFile)
      params.append('removeImage', form.removeImage ? '1' : '0')
      params.append('opening_time', form.opening_time)
      params.append('closeing_time', form.closeing_time)
      params.append('latitude', form.latitude)
      params.append('longitude', form.longitude)
      const res = await api.post('saveShop', params, 'Admin')
      if (res.data.status === 1) {
        await dispatch('fetchShopList')// 刷新商铺列表
        Message.success(t(res.data.msg))
      } else {
        Message.error(t(res.data.msg))
      }
      return res
    },
    /**
     * @description: 从状态栏单独更新商铺主动打烊状态
     */
    async fetchSetShopManualClosed ({ dispatch }, { shopId, manualClosed, description }) {
      const params = new URLSearchParams()
      params.append('shop_id', shopId)
      params.append('manual_closed', manualClosed ? '1' : '0')
      params.append('manual_closed_desc', description || '')
      const res = await api.post('setShopManualClosed', params, 'Admin')
      if (Number(res.data.status) === 1) {
        await dispatch('fetchShopList')
      }
      return res
    },
    /**
     * @description: 获取管理员列表
     */
    async fetchAdminList ({ commit }) {
      const res = await api.get('getAdminList', 'Admin')
      if (!res.data || !Array.isArray(res.data.adminList) || ![0, 1].includes(Number(res.data.status)) || (Number(res.data.status) === 0 && res.data.adminList.length > 0)) {
        throw new Error((res.data && res.data.msg) || '数据读取失败')
      }
      if (res.data.status === 1) {
        res.data.adminList.forEach((item, index) => {
          // 判断如果是当前登录用户  最后登录时间字段 从本地缓存中获取 PS:既登陆前缓存到本地的“最后登陆时间” 因为登录后 服务端会把当前登录的时间刷到lasttime字段
          if (String(item.id) === String(store.state.user.id)) {
            item.lasttime = store.state.user.lasttime
          }
          // 头像字段反序列化
          item.photo = parseJsonArray(item.photo, true)
          item.photo.forEach((photo, i) => {
            res.data.adminList[index].photo[i] = settings.state.photoPath + photo // 把绝对路径加上
          })
        })
        commit('setAdminList', res.data.adminList)
      } else {
        commit('setAdminList', [])
      }
    },
    /**
     * @description: 添加新的管理员
     * @param {*} 管理员信息表单
     * @return {*} 服务器返回值
     */
    async fetchAddAdmin ({ dispatch }, form) {
      const params = new URLSearchParams()
      params.append('name', form.name)
      params.append('uname', form.uname)
      params.append('pwd', form.pwd)
      params.append('role', form.role == null ? '' : form.role)
      params.append('shop_id', form.shop_id)
      params.append('upFile', form.upFile)
      const res = await api.post('addAdmin', params, 'Admin')
      if (res.data.status === 1) {
        await dispatch('fetchAdminList')// 刷新用户列表
        Message.success(t(res.data.msg))
      } else {
        Message.error(t(res.data.msg))
      }
      return res
    },
    /**
     * @description: 更新管理员账号信息
     * @param {*} 管理员信息表单
     * @return {*} 服务器返回值
     */
    async fetchSaveAdmin ({ dispatch }, form) {
      const params = new URLSearchParams()
      params.append('id', form.id)
      params.append('shop_id', form.shop_id)
      params.append('uname', form.uname)
      params.append('pwd', form.pwd)
      if (form.role != null) params.append('role', form.role)
      params.append('upFile', form.upFile)
      params.append('oldFile', form.oldFile)
      params.append('removeImage', form.removeImage ? '1' : '0')
      const res = await api.post('saveAdmin', params, 'Admin')
      if (res.data.status === 1) {
        if (String(form.id) === String(store.state.user.id)) {
          store.commit('user/updateProfile', {
            uname: form.uname,
            photo: form.upFile || (form.removeImage ? '' : store.state.user.photo)
          })
        }
        if (store.state.user.is_god === true || store.state.user.is_owner === true) {
          await dispatch('fetchAdminList')// 刷新用户列表
        }
        Message.success(t(res.data.msg))
      } else {
        Message.error(t(res.data.msg))
      }
      return res
    },
    /**
     * @description: 删除账号
     * @param {*} 多选id组
     */
    async fetchDelAdmin ({ dispatch }, idArr) {
      if (idArr.length === 0) {
        Message.error(t('未勾选记录'))
      } else {
        MessageBox.confirm(t('请谨慎操作 确认删除?'), t('提示'), {
          confirmButtonText: t('确定'),
          cancelButtonText: t('取消'),
          type: 'warning'
        }).then(() => {
          const params = new URLSearchParams()
          params.append('idArr', idArr)
          api.post('deleteAdmin', params, 'Admin').then(res => {
            if (res.data.status === 1) {
              Message.success(t(res.data.msg))
              dispatch('fetchAdminList')// 刷新管理员列表
            } else {
              Message.error(t(res.data.msg))
            }
          })
        }).catch(() => {
          Message.info(t('取消操作'))
        })
      }
    },
    /**
     * @description: 获取飞机列表
     */
    async fetchAirList ({ commit, state, isDataSyncCurrent = () => true }) {
      const isLatest = beginTaskRefresh('fetchAirList', state)
      const res = await api.get('getAirList')
      if (!res.data || !Array.isArray(res.data.airList) || ![0, 1].includes(Number(res.data.status)) || (Number(res.data.status) === 0 && res.data.airList.length > 0)) {
        throw new Error((res.data && res.data.msg) || '数据读取失败')
      }
      if (!isLatest() || !isDataSyncCurrent()) return state.airList
      const incomingList = Array.isArray(res.data.airList) ? res.data.airList : []
      if (res.data.status === 1) {
        const previousById = new Map(state.airList.map(plane => [String(plane.id), plane]))
        const retainedIds = new Set()
        const incomingIds = new Set(incomingList.map(plane => String(plane.id)))
        incomingList.forEach(plane => {
          const previous = previousById.get(String(plane.id))
          const sameIdentity = previous &&
            String(previous.shop_id) === String(plane.shop_id) &&
            String(previous.plane_code) === String(plane.plane_code)
          if (sameIdentity && previous.planeState) {
            plane.planeState = previous.planeState
            retainedIds.add(String(plane.id))
          } else {
            plane.planeState = createPlaneState()
            if (previous) clearPlaneStateTimers(previous.planeState)
          }
        })
        state.airList.forEach(plane => {
          if (!retainedIds.has(String(plane.id)) && !incomingIds.has(String(plane.id))) {
            clearPlaneStateTimers(plane.planeState)
          }
        })
        commit('setAirList', incomingList)
      } else {
        state.airList.forEach(plane => clearPlaneStateTimers(plane.planeState))
        commit('setAirList', [])
      }
      return incomingList
    },
    /**
     * @description: 创建新飞机
     * @param {*} form 表单.飞机信息
     * @return {*} 服务器返回信息
     */
    async fetchAddAir ({ dispatch }, form) {
      const params = new URLSearchParams()
      params.append('shop_id', form.shop_id)
      params.append('name', form.name)
      params.append('color', form.color)
      params.append('date', form.date)
      params.append('onoff', form.onoff ? '1' : '0')
      params.append('desc', form.desc)
      params.append('module_mask', String(form.module_mask || 0))
      params.append('camera_fov', String(form.camera_fov || 55))
      params.append('bind_class_id', form.bind_class_id) // ✅ 添加机型绑定字段

      const res = await api.post('addAir', params)
      if (res.data.status === 1) {
        await dispatch('fetchAirList')
        Message.success(t(res.data.msg))
      } else {
        Message.error(t(res.data.msg))
      }
      return res
    },
    /**
     * @description: 更新新飞机数据
     * @param {*} form 表单.飞机信息
     * @return {*} 服务器返回信息
     */
    async fetchSaveAir ({ dispatch }, form) {
      const params = new URLSearchParams()
      params.append('shop_id', form.shop_id)
      params.append('name', form.name)
      params.append('color', form.color)
      params.append('date', form.date)
      params.append('onoff', form.onoff ? '1' : '0')
      params.append('desc', form.desc)
      params.append('id', form.id)
      params.append('module_mask', String(form.module_mask || 0))
      params.append('camera_fov', String(form.camera_fov || 55))
      params.append('bind_class_id', form.bind_class_id) // ✅ 添加机型绑定字段

      const res = await api.post('saveAir', params)
      if (res.data.status === 1) {
        await dispatch('fetchAirList')
        Message.success(t(res.data.msg))
      } else {
        Message.error(t(res.data.msg))
      }
      return res
    },
    /**
     * @description: 在飞机列表中快捷切换启用状态
     */
    async fetchSetAirOnOff ({ dispatch }, form) {
      const params = new URLSearchParams()
      params.append('id', form.id)
      params.append('onoff', form.onoff)
      const res = await api.post('setAirOnOff', params)
      if (res.data.status === 1) {
        await dispatch('fetchAirList')
        Message.success(t(res.data.msg))
      } else {
        Message.error(t(res.data.msg))
      }
      return res
    },
    /**
     * @description: 删除指定序号飞机
     * @param {*} idArray 飞机序号数组
     */
    fetchDelAir ({ dispatch }, idArr) {
      if (idArr.length === 0) {
        Message.error(t('未勾选记录'))
      } else {
        MessageBox.confirm(t('请谨慎操作 确认删除?'), t('提示'), {
          confirmButtonText: t('确定'),
          cancelButtonText: t('取消'),
          type: 'warning'
        }).then(() => {
          const params = new URLSearchParams()
          params.append('idArr', idArr)
          api.post('deleteAir', params).then(res => {
            if (res.data.status === 1) {
              Message.success(t(res.data.msg))
              dispatch('fetchAirList')// 刷新飞机列表
            } else {
              Message.error(t(res.data.msg))
            }
          })
        }).catch(() => {
          Message.info(t('取消操作'))
        })
      }
    },
    /**
     * @description: 获取机型列表
     */
    async fetchPlaneClassList ({ commit }) {
      const prefix = store.state.user.is_god === true ? 'God' : 'Plane'
      const res = await api.get('getPlaneClassList', prefix)
      if (!res.data || !Array.isArray(res.data.planeClassList) || ![0, 1].includes(Number(res.data.status)) || (Number(res.data.status) === 0 && res.data.planeClassList.length > 0)) {
        throw new Error((res.data && res.data.msg) || '数据读取失败')
      }
      if (res.data.status === 1) {
        commit('setPlaneClassList', res.data.planeClassList)
      } else {
        commit('setPlaneClassList', [])
      }
      return res
    },
    /**
     *  @description: 获取飞机模块列表
     * */
    async fetchPlaneModelList ({ commit }) {
      const res = await api.get('getPlaneModelList')
      if (!res.data || !Array.isArray(res.data.planeModelList) || ![0, 1].includes(Number(res.data.status)) || (Number(res.data.status) === 0 && res.data.planeModelList.length > 0)) {
        throw new Error((res.data && res.data.msg) || '数据读取失败')
      }
      if (res.data.status === 1) {
        commit('setPlaneModelList', res.data.planeModelList)
      } else {
        commit('setPlaneModelList', [])
      }
      return res
    },
    /**
     * @description: 创建新机型
     * @param {*} form 表单.机型信息
     * @return {*} 服务器返回信息
     */
    async fetchAddPlaneClass ({ dispatch }, form) {
      const params = new URLSearchParams()
      params.append('class_name', form.class_name)
      params.append('type_code', form.type_code)
      params.append('wheelbase', form.wheelbase)
      params.append('category', form.category)
      params.append('weight_max', form.weight_max)
      params.append('battery_series', form.battery_series)
      params.append('describe', form.describe)
      const res = await api.post('addPlaneClass', params, 'God')
      if (res.data.status === 1) {
        await dispatch('fetchPlaneClassList')// 刷新 机型列表
        await dispatch('fetchAirList')// 刷新飞机列表
        Message.success(t(res.data.msg))
      } else {
        Message.error(t(res.data.msg))
      }
      return res
    },
    /**
     * @description: 创建新机型
     * @param {*} form 表单.机型信息
     * @return {*} 服务器返回信息
     */
    async fetchSavePlaneClass ({ dispatch }, form) {
      const params = new URLSearchParams()
      params.append('id', form.id)
      params.append('class_name', form.class_name)
      params.append('type_code', form.type_code)
      params.append('wheelbase', form.wheelbase)
      params.append('category', form.category)
      params.append('weight_max', form.weight_max)
      params.append('battery_series', form.battery_series)
      params.append('describe', form.describe)
      const res = await api.post('savePlaneClass', params, 'God')
      if (res.data.status === 1) {
        await dispatch('fetchPlaneClassList')// 刷新 机型列表
        await dispatch('fetchAirList')// 刷新飞机列表
        Message.success(t(res.data.msg))
      } else {
        Message.error(t(res.data.msg))
      }
      return res
    },
    /**
     * @description: 删除指定序号 机型
     * @param {*} idArray 机型序号数组
     */
    fetchDelPlaneClass ({ dispatch }, idArr) {
      if (idArr.length === 0) {
        Message.error(t('未勾选记录'))
      } else {
        MessageBox.confirm(t('请谨慎操作 确认删除?'), t('提示'), {
          confirmButtonText: t('确定'),
          cancelButtonText: t('取消'),
          type: 'warning'
        }).then(() => {
          const params = new URLSearchParams()
          params.append('idArr', idArr)
          api.post('deletePlaneClass', params, 'God').then(res => {
            if (res.data.status === 1) {
              Message.success(t(res.data.msg))
              dispatch('fetchPlaneClassList')// 刷新 机型列表
            } else {
              Message.error(t(res.data.msg))
            }
          })
        }).catch(() => {
          Message.info(t('取消操作'))
        })
      }
    },
    /**
 * @description: 获取站点列表
 */
    async fetchSiteList ({ commit, state }) {
      const isLatest = beginTaskRefresh('fetchSiteList', state)
      const res = await api.get('getSiteList')
      if (!res.data || !Array.isArray(res.data.siteList) || ![0, 1].includes(Number(res.data.status)) || (Number(res.data.status) === 0 && res.data.siteList.length > 0)) {
        throw new Error((res.data && res.data.msg) || '数据读取失败')
      }
      if (!isLatest()) return res
      if (res.data.status === 1) {
        const siteList = Array.isArray(res.data.siteList) ? res.data.siteList : []
        siteList.forEach((site, index) => {
          // 二维码路径
          site.qr = settings.state.qrPath + site.qr

          // 反序列化 photo（数据库存的是字符串，如 "[]")
          site.photo = parseJsonArray(site.photo, true)

          // 拼接站点图片绝对路径
          site.photo.forEach((photo, i) => {
            siteList[index].photo[i] = settings.state.sitePath + photo
          })

          // 站点绑定航线和执行飞机统一解析为数组，兼容旧逗号字符串和新 JSON 数组
          const runtime = normalizeSiteRuntime(site)
          siteList[index].bind_route_array = runtime.bindRouteArray
          siteList[index].runing_array = runtime.runingArray
        })
        commit('setSiteList', siteList)
      } else {
        commit('setSiteList', [])
      }
    },

    /**
     * @description: 创建新站点
     * @param {*} form 表单.站点信息
     * @return {*} 服务器返回信息
     */
    async fetchAddSite ({ dispatch }, form) {
      const params = new URLSearchParams()
      params.append('shop_id', form.shop_id)
      params.append('sitename', form.sitename)
      params.append('desc', form.desc)
      params.append('color', form.color)
      params.append('upFile', form.upFile)
      params.append('size', form.size)
      params.append('bindroute', JSON.stringify(form.bindroute || []))
      params.append('latitude', form.latitude)
      params.append('longitude', form.longitude)
      params.append('photoUpFile', form.photoUpFile) // 站点图片
      const res = await api.post('addSite', params)
      if (res.data.status === 1) {
        await dispatch('fetchSiteList')// 刷新站点列表
        if (((store.state.user.permissions || []).includes('plane.route.view') || (store.state.user.permissions || []).includes('terminal.dashboard'))) dispatch('fetchRouteList')
        if ((store.state.user.permissions || []).includes('terminal.dashboard')) dispatch('fetchPaidOrderList')
        Message.success(t(res.data.msg))
      } else {
        Message.error(t(res.data.msg))
      }
      return res
    },
    /**
     * @description: 创建新站点
     * @param {*} form 表单.站点信息
     * @return {*} 服务器返回信息
     */
    async fetchSaveSite ({ dispatch }, form) {
      const params = new URLSearchParams()
      params.append('id', form.id)
      params.append('shop_id', form.shop_id)
      params.append('sitename', form.sitename)
      params.append('desc', form.desc)
      params.append('color', form.color)
      params.append('upFile', form.upFile)
      params.append('size', form.size)
      params.append('size_changed', form.size_changed ? '1' : '0')
      params.append('bindroute', JSON.stringify(form.bindroute || []))
      params.append('latitude', form.latitude)
      params.append('longitude', form.longitude)
      params.append('photoUpFile', form.photoUpFile) // 站点图片
      params.append('removePhoto', form.removePhoto ? '1' : '0')
      const res = await api.post('saveSite', params)
      if (res.data.status === 1) {
        await dispatch('fetchSiteList')// 刷新站点列表
        if (((store.state.user.permissions || []).includes('plane.route.view') || (store.state.user.permissions || []).includes('terminal.dashboard'))) dispatch('fetchRouteList')
        if ((store.state.user.permissions || []).includes('terminal.dashboard')) dispatch('fetchPaidOrderList')
        Message.success(t(res.data.msg))
      } else {
        Message.error(t(res.data.msg))
      }
      return res
    },
    /**
     * @description: 删除指定序号站点
     * @param {*} idArray 站点序号数组
     */
    fetchDelSite ({ dispatch }, idArr) {
      if (idArr.length === 0) {
        Message.error(t('未勾选记录'))
      } else {
        MessageBox.confirm(t('请谨慎操作 确认删除?'), t('提示'), {
          confirmButtonText: t('确定'),
          cancelButtonText: t('取消'),
          type: 'warning'
        }).then(() => {
          const params = new URLSearchParams()
          params.append('idArr', idArr)
          api.post('deleteSite', params).then(res => {
            if (res.data.status === 1) {
              Message.success(t(res.data.msg))
              dispatch('fetchSiteList')// 刷新站点列表
              if (((store.state.user.permissions || []).includes('plane.route.view') || (store.state.user.permissions || []).includes('terminal.dashboard'))) dispatch('fetchRouteList')
              if ((store.state.user.permissions || []).includes('terminal.dashboard')) dispatch('fetchPaidOrderList')
            } else {
              Message.error(t(res.data.msg))
            }
          })
        }).catch(() => {
          Message.info(t('取消操作'))
        })
      }
    },
    /**
     * @description: 获取航线列表
     */
    async fetchRouteList ({ commit, state }) {
      const isLatest = beginTaskRefresh('fetchRouteList', state)
      const res = await api.get('getRouteList')
      if (!res.data || !Array.isArray(res.data.routeList) || ![0, 1].includes(Number(res.data.status)) || (Number(res.data.status) === 0 && res.data.routeList.length > 0)) {
        throw new Error((res.data && res.data.msg) || '数据读取失败')
      }
      if (!isLatest()) return state.routeList
      if (res.data.status === 1) {
        const routeList = Array.isArray(res.data.routeList) ? res.data.routeList : []
        commit('setRouteList', routeList)
        return routeList
      } else {
        commit('setRouteList', [])
        return []
      }
    },
    /**
     * @description 将终端设计器生成的 JSON 上传为临时航线文件，兼容线上旧版接口
     */
    async fetchUploadDesignedRoute (context, routeData) {
      const params = new FormData()
      const file = new Blob([routeData], { type: 'application/json;charset=utf-8' })
      params.append('file', file, `route-${Date.now()}.json`)
      return api.post('upTxtFile', params)
    },
    /**
     * @description: 创建新航线
     * @param {*} form 表单.航线信息
     * @return {*} 服务器返回信息
     */
    async fetchAddRoute ({ dispatch }, form) {
      const params = new URLSearchParams()
      params.append('shop_id', form.shop_id)
      params.append('name', form.name)
      params.append('color', form.color)
      params.append('desc', form.desc)
      params.append('upFile', form.upFile)
      params.append('route_data', form.upFile ? '' : (form.route_data || ''))
      const res = await api.post('addRoute', params)
      if (res.data.status === 1) {
        await dispatch('fetchRouteList')// 刷新航线列表
        if (((store.state.user.permissions || []).includes('plane.site.view') || (store.state.user.permissions || []).includes('terminal.dashboard'))) dispatch('fetchSiteList')
        if ((store.state.user.permissions || []).includes('terminal.dashboard')) dispatch('fetchPaidOrderList')
        Message.success(t(res.data.msg))
      } else {
        Message.error(t(res.data.msg))
      }
      return res
    },
    /**
     * @description: 更新航线
     * @param {*} form 表单.航线信息
     * @return {*} 服务器返回信息
     */
    async fetchSaveRoute ({ dispatch }, form) {
      const params = new URLSearchParams()
      params.append('id', form.id)
      params.append('shop_id', form.shop_id)
      params.append('name', form.name)
      params.append('color', form.color)
      params.append('desc', form.desc)
      params.append('upFile', form.upFile)
      params.append('route_data', form.upFile ? '' : (form.route_data || ''))
      const res = await api.post('saveRoute', params)
      if (res.data.status === 1) {
        await dispatch('fetchRouteList')// 刷新航线列表
        if (((store.state.user.permissions || []).includes('plane.site.view') || (store.state.user.permissions || []).includes('terminal.dashboard'))) dispatch('fetchSiteList')
        if ((store.state.user.permissions || []).includes('terminal.dashboard')) dispatch('fetchPaidOrderList')
        Message.success(t(res.data.msg))
      } else {
        Message.error(t(res.data.msg))
      }
      return res
    },
    /**
     * @description: 删除指定序号航线
     * @param {*} idArray 航线序号数组
     */
    fetchDelRoute ({ dispatch }, idArr) {
      if (idArr.length === 0) {
        Message.error(t('未勾选记录'))
      } else {
        MessageBox.confirm(t('请谨慎操作 确认删除?'), t('提示'), {
          confirmButtonText: t('确定'),
          cancelButtonText: t('取消'),
          type: 'warning'
        }).then(() => {
          const params = new URLSearchParams()
          params.append('idArr', idArr)
          api.post('deleteRoute', params).then(res => {
            if (res.data.status === 1) {
              Message.success(t(res.data.msg))
              dispatch('fetchRouteList')// 刷新航线列表
              if (((store.state.user.permissions || []).includes('plane.site.view') || (store.state.user.permissions || []).includes('terminal.dashboard'))) dispatch('fetchSiteList')
              if ((store.state.user.permissions || []).includes('terminal.dashboard')) dispatch('fetchPaidOrderList')
            } else {
              Message.error(t(res.data.msg))
            }
          })
        }).catch(() => {
          Message.info(t('取消操作'))
        })
      }
    },
    /**
     * @description: 获取禁飞区数据
     */
    async fetchNoflyData ({ commit }) {
      const res = await api.get('getNoflyData', 'Plane')
      if (!res.data || !Array.isArray(res.data.restrictionList) || ![0, 1].includes(Number(res.data.status)) || (Number(res.data.status) === 0 && res.data.restrictionList.length > 0)) {
        throw new Error((res.data && res.data.msg) || '数据读取失败')
      }

      if (res.data.status === 1) {
        commit('setNoflyData', res.data.restrictionList)
      } else {
        commit('setNoflyData', [])
      }
      return res
    },
    /**
     * @description: 获取分类列表
     * @return {*} 列表
     */
    async fetchCategoryList ({ commit }) {
      const res = await api.get('getCategoryList', 'Admin')
      if (!res.data || !Array.isArray(res.data.categoryList) || ![0, 1].includes(Number(res.data.status)) || (Number(res.data.status) === 0 && res.data.categoryList.length > 0)) {
        throw new Error((res.data && res.data.msg) || '数据读取失败')
      }
      if (res.data.status === 1) {
        res.data.categoryList.forEach((category, index) => {
          category.photo = parseJsonArray(category.photo, true)
          category.photo.forEach((photo, i) => {
            res.data.categoryList[index].photo[i] = settings.state.listPath + photo // 把绝对路径加上
          })
        })
        commit('setCategoryList', res.data.categoryList)
      } else {
        commit('setCategoryList', [])
      }
      return res
    },
    /**
     * @description: 添加分类信息
     * @param {*} 分类信息表单
     * @return {*} 服务器返回值
     */
    async fetchAddCategory ({ dispatch }, form) {
      const params = new URLSearchParams()
      params.append('shop_id', form.shop_id)
      params.append('id', form.id)
      params.append('path', form.path)
      params.append('name', form.name)
      params.append('sort', form.sort)
      params.append('show', form.show)
      params.append('desc', form.desc)
      params.append('upFile', form.upFile)
      const res = await api.post('addCategory', params, 'Admin')
      if (res.data.status === 1) {
        await dispatch('fetchCategoryList')// 刷新商铺列表
        Message.success(t(res.data.msg))
      } else {
        Message.error(t(res.data.msg))
      }
      return res
    },
    /**
     * @description: 更新分类信息
     * @param {*} 分类信息表单
     * @return {*} 服务器返回值
     */
    async fetchSaveCategory ({ dispatch }, form) {
      const params = new URLSearchParams()
      params.append('shop_id', form.shop_id)
      params.append('id', form.id)
      params.append('name', form.name)
      params.append('sort', form.sort)
      params.append('show', form.show)
      params.append('desc', form.desc)
      params.append('upFile', form.upFile)
      params.append('oldFile', form.oldFile)
      params.append('removeImage', form.removeImage ? '1' : '0')
      const res = await api.post('saveCategory', params, 'Admin')
      if (res.data.status === 1) {
        await dispatch('fetchCategoryList')// 刷新商铺列表
        Message.success(t(res.data.msg))
      } else {
        Message.error(t(res.data.msg))
      }
      return res
    },

    /**
     * @description: 删除分类
     */
    // eslint-disable-next-line camelcase
    async fetchDelCategory ({ dispatch }, form) {
      const params = new URLSearchParams()
      params.append('delIdArr', form.delIdArr)
      params.append('shop_id', form.shop_id)
      api.post('deleteCategory', params, 'Admin').then(res => {
        if (res.data.status === 1) {
          Message.success(t(res.data.msg))
          dispatch('fetchCategoryList')// 刷新分类列表
        } else {
          Message.error(t(res.data.msg))
        }
      })
    },
    /**
     * @description: 获取Banner列表
     * @return {*} 列表
     */
    async fetchNoticeList ({ commit }) {
      const res = await api.get('getNoticeList', 'Admin')
      if (!res.data || !Array.isArray(res.data.noticeList) || ![0, 1].includes(Number(res.data.status)) ||
        (Number(res.data.status) === 0 && res.data.noticeList.length > 0)) {
        throw new Error((res.data && res.data.msg) || '滚动通知列表读取失败')
      }
      commit('setNoticeList', res.data.noticeList)
      return res
    },
    async writeNotice ({ dispatch }, { action, form }) {
      const params = new URLSearchParams()
      Object.keys(form).forEach(key => params.append(key, form[key] == null ? '' : form[key]))
      const res = await api.post(action, params, 'Admin')
      if (Number(res.data.status) !== 1) {
        Message.error(t(res.data.msg || '操作失败'))
        return res
      }
      Message.success(t(res.data.msg))
      try {
        await dispatch('fetchNoticeList')
        return res
      } catch (error) {
        Message.warning(t('操作已成功，但列表刷新失败，请刷新列表后继续操作'))
        return { ...res, noticeRefreshFailed: true }
      }
    },
    fetchAddNotice ({ dispatch }, form) { return dispatch('writeNotice', { action: 'addNotice', form }) },
    fetchSaveNotice ({ dispatch }, form) { return dispatch('writeNotice', { action: 'saveNotice', form }) },
    fetchDelNotice ({ dispatch }, form) { return dispatch('writeNotice', { action: 'deleteNotice', form }) },
    async fetchBannerList ({ commit }) {
      const res = await api.get('getBannerList', 'Admin')
      if (!res.data || !Array.isArray(res.data.bannerList) || ![0, 1].includes(Number(res.data.status)) ||
        (Number(res.data.status) === 0 && res.data.bannerList.length > 0)) {
        // 接口拒绝/异常不是空列表，保留上次已确认的数据并交给调用方提示。
        throw new Error((res.data && res.data.msg) || '轮播图列表读取失败')
      }
      if (Number(res.data.status) === 1) {
        res.data.bannerList.forEach((banner, index) => {
          // 兼容 photo 为空 / 非JSON 的情况
          banner.photo = parseJsonArray(banner.photo, true)

          banner.photo.forEach((photo, i) => {
            res.data.bannerList[index].photo[i] = settings.state.bannerPath + photo // 拼接绝对路径
          })
        })
        commit('setBannerList', res.data.bannerList)
      } else {
        commit('setBannerList', [])
      }
      return res
    },
    /**
 * @description: 添加Banner信息
 * @param {*} Banner信息表单
 * @return {*} 服务器返回值
 */
    async fetchAddBanner ({ dispatch }, form) {
      const params = new URLSearchParams()
      params.append('shop_id', form.shop_id === null ? '' : form.shop_id)
      params.append('name', form.name)
      params.append('sort', form.sort)
      params.append('type', form.type) // index / product
      params.append('show', form.show)
      params.append('action', form.action)
      params.append('upFile', form.upFile)

      const res = await api.post('addBanner', params, 'Admin')
      if (Number(res.data.status) === 1) {
        Message.success(t(res.data.msg))
        return dispatch('refreshBannerAfterWrite', res)
      } else {
        Message.error(t(res.data.msg))
      }
      return res
    },

    /**
     * @description: 更新Banner信息
     * @param {*} Banner信息表单
     * @return {*} 服务器返回值
     */
    async fetchSaveBanner ({ dispatch }, form) {
      const params = new URLSearchParams()
      params.append('shop_id', form.shop_id === null ? '' : form.shop_id)
      params.append('id', form.id)
      params.append('name', form.name)
      params.append('sort', form.sort)
      params.append('type', form.type) // index / product
      params.append('show', form.show)
      params.append('action', form.action)
      params.append('upFile', form.upFile)
      params.append('oldFile', form.oldFile)

      const res = await api.post('saveBanner', params, 'Admin')
      if (Number(res.data.status) === 1) {
        Message.success(t(res.data.msg))
        return dispatch('refreshBannerAfterWrite', res)
      } else {
        Message.error(t(res.data.msg))
      }
      return res
    },

    async refreshBannerAfterWrite ({ dispatch }, response) {
      try {
        await dispatch('fetchBannerList')
        return response
      } catch (error) {
        // 写入已经成功；不能将随后的读取失败当成写失败，诱发重复创建。
        Message.warning(t('操作已成功，但列表刷新失败，请刷新列表后继续操作'))
        return { ...response, bannerRefreshFailed: true }
      }
    },
    /**
     * @description: 删除Banner
     */
    // eslint-disable-next-line camelcase
    async fetchDelBanner ({ dispatch }, form) {
      const params = new URLSearchParams()
      params.append('id', form.id)
      params.append('shop_id', form.shop_id === null ? '' : form.shop_id)

      const res = await api.post('deleteBanner', params, 'Admin')
      if (Number(res.data.status) === 1) {
        Message.success(t(res.data.msg))
        return dispatch('refreshBannerAfterWrite', res)
      }
      Message.error(t(res.data.msg))
      return res
    },

    /**
     * @description: 获取商品spu列表
     * @return {*} 列表
     */
    async fetchSpuList ({ commit }) {
      const res = await api.get('getSpuList', 'Admin')
      if (!res.data || !Array.isArray(res.data.spuList) || ![0, 1].includes(Number(res.data.status)) || (Number(res.data.status) === 0 && res.data.spuList.length > 0)) {
        throw new Error((res.data && res.data.msg) || '数据读取失败')
      }
      if (res.data.status === 1) {
        res.data.spuList.forEach((spu, index) => {
          spu.pro_tag = parseJsonArray(spu.pro_tag)
          spu.bind_sku = parseJsonArray(spu.bind_sku)
          spu.photo = parseJsonArray(spu.photo, true)
          spu.photo.forEach((photo, i) => {
            res.data.spuList[index].photo[i] = settings.state.spuPath + photo // 把绝对路径加上
          })
        })
        commit('setSpuList', res.data.spuList)
      } else {
        commit('setSpuList', [])
      }
      return res
    },
    /**
     * @description: spu排序
     * @param {*} spu信息
     */
    async fetchOrderSpu ({ dispatch }, form) {
      const params = new URLSearchParams()
      params.append('shop_id', form.shop_id)
      params.append('id', form.id)
      params.append('sort', form.sort)
      const res = await api.post('orderSpu', params, 'Admin')
      if (res.data.status === 1) {
        await dispatch('fetchSpuList')// 刷新商品列表
        Message.success(t(res.data.msg))
      } else {
        Message.error(t(res.data.msg))
      }
      return res
    },
    /**
     * @description: 商品前端显示隐藏
     * @param {*} 商品信息
     */
    async fetchShowSpu ({ dispatch }, form) {
      const params = new URLSearchParams()
      params.append('shop_id', form.shop_id)
      params.append('id', form.id)
      params.append('show', form.show)
      const res = await api.post('showSpu', params, 'Admin')
      if (res.data.status === 1) {
        await dispatch('fetchSpuList')// 刷新商品列表
        Message.success(t(res.data.msg))
      } else {
        Message.error(t(res.data.msg))
      }
      return res
    },
    /**
     * @description: 商品前端推荐位
     * @param {*} 商品信息
     */
    async fetchRecommendSpu ({ dispatch }, form) {
      const params = new URLSearchParams()
      params.append('shop_id', form.shop_id)
      params.append('id', form.id)
      params.append('recommend', form.recommend)
      const res = await api.post('recommendSpu', params, 'Admin')
      if (res.data.status === 1) {
        await dispatch('fetchSpuList')// 刷新商品列表
        Message.success(t(res.data.msg))
      } else {
        Message.error(t(res.data.msg))
      }
      return res
    },
    /**
     * @description: 添加商品 spu
     * @param {*} form 表单.商品信息
     * @return {*} 服务器返回信息
     */
    async fetchAddSpu ({ dispatch }, form) {
      const params = new URLSearchParams()
      params.append('shop_id', form.shop_id)
      params.append('path', form.path)
      params.append('name', form.name)
      params.append('spu_number', form.spu_number)
      params.append('content', form.content || '')
      params.append('prepare_minutes', form.prepare_minutes)
      params.append('sort', form.sort)
      params.append('hot', form.hot)
      params.append('pro_tag', JSON.stringify(form.pro_tag))
      params.append('bind_sku', JSON.stringify(form.bind_sku))
      params.append('upFiles', JSON.stringify((form.imageFiles || []).filter(file => file.isNew).map(file => file.storedName)))
      params.append('oldFiles', JSON.stringify((form.imageFiles || []).filter(file => !file.isNew).map(file => file.storedName || file.name)))
      if (form.recommend) {
        params.append('recommend', '1')
      } else {
        params.append('recommend', '0')
      }
      if (form.show) {
        params.append('show', '1')
      } else {
        params.append('show', '0')
      }
      const res = await api.post('addSpu', params, 'Admin')
      if (res.data.status === 1) {
        await dispatch('fetchSpuList')// 刷新商品列表
        Message.success(t(res.data.msg))
      } else {
        Message.error(t(res.data.msg))
      }
      return res
    },
    /**
     * @description: 更新商品 spu
     * @param {*} form 表单.商品信息
     * @return {*} 服务器返回信息
     */
    async fetchSaveSpu ({ dispatch }, form) {
      const params = new URLSearchParams()
      params.append('id', form.id)
      params.append('shop_id', form.shop_id)
      params.append('path', form.path)
      params.append('name', form.name)
      params.append('spu_number', form.spu_number)
      params.append('content', form.content || '')
      params.append('prepare_minutes', form.prepare_minutes)
      params.append('sort', form.sort)
      params.append('hot', form.hot)
      params.append('pro_tag', JSON.stringify(form.pro_tag))
      params.append('bind_sku', JSON.stringify(form.bind_sku))
      params.append('upFiles', JSON.stringify((form.imageFiles || []).filter(file => file.isNew).map(file => file.storedName)))
      params.append('oldFiles', JSON.stringify((form.imageFiles || []).filter(file => !file.isNew).map(file => file.storedName || file.name)))
      if (form.recommend) {
        params.append('recommend', '1')
      } else {
        params.append('recommend', '0')
      }
      if (form.show) {
        params.append('show', '1')
      } else {
        params.append('show', '0')
      }
      const res = await api.post('saveSpu', params, 'Admin')
      if (res.data.status === 1) {
        await dispatch('fetchSpuList')// 刷新商品列表
        Message.success(t(res.data.msg))
      } else {
        Message.error(t(res.data.msg))
      }
      return res
    },
    /**
     * @description: 删除spu
     * @param {*} 多选id组
     */
    async fetchDelSpu ({ dispatch }, idArr) {
      if (idArr.length === 0) {
        Message.error(t('未勾选记录'))
      } else {
        MessageBox.confirm(t('请谨慎操作 确认删除?'), t('提示'), {
          confirmButtonText: t('确定'),
          cancelButtonText: t('取消'),
          type: 'warning'
        }).then(() => {
          const params = new URLSearchParams()
          params.append('idArr', idArr)
          api.post('deleteSpu', params, 'Admin').then(res => {
            if (res.data.status === 1) {
              Message.success(t(res.data.msg))
              dispatch('fetchSpuList')// 刷新列表
            } else {
              Message.error(t(res.data.msg))
            }
          })
        }).catch(() => {
          Message.info(t('取消操作'))
        })
      }
    },
    /**
     * @description: 获取商品sku列表
     * @return {*} 列表
     */
    async fetchSkuList ({ commit }) {
      const res = await api.get('getSkuList', 'Admin')
      if (!res.data || !Array.isArray(res.data.skuList) || ![0, 1].includes(Number(res.data.status)) || (Number(res.data.status) === 0 && res.data.skuList.length > 0)) {
        throw new Error((res.data && res.data.msg) || '数据读取失败')
      }
      if (res.data.status === 1) {
        res.data.skuList.forEach((sku, index) => {
          sku.photo = parseJsonArray(sku.photo, true)
          sku.photo.forEach((photo, i) => {
            res.data.skuList[index].photo[i] = settings.state.skuPath + photo // 把绝对路径加上
          })
        })
        commit('setSkuList', res.data.skuList)
      } else {
        commit('setSkuList', [])
      }
      return res
    },
    /**
     * @description: 添加商品 sku
     * @param {*} form 表单.商品信息
     * @return {*} 服务器返回信息
     */
    async fetchAddSku ({ dispatch }, form) {
      const params = new URLSearchParams()
      params.append('shop_id', form.shop_id)
      params.append('name', form.name)
      params.append('sku_number', form.sku_number)
      params.append('price', form.price)
      params.append('unit', form.unit)
      params.append('weight', form.weight)
      params.append('stock', form.stock)
      params.append('purchase_channel', form.purchase_channel)
      params.append('upFiles', JSON.stringify((form.imageFiles || []).filter(file => file.isNew).map(file => file.storedName)))
      params.append('oldFiles', JSON.stringify((form.imageFiles || []).filter(file => !file.isNew).map(file => file.storedName || file.name)))
      const res = await api.post('addSku', params, 'Admin')
      if (res.data.status === 1) {
        await dispatch('fetchSkuList')// 刷新商品列表
        Message.success(t(res.data.msg))
      } else {
        Message.error(t(res.data.msg))
      }
      return res
    },
    /**
     * @description: 更新商品 sku
     * @param {*} form 表单.商品信息
     * @return {*} 服务器返回信息
     */
    async fetchSaveSku ({ dispatch }, form) {
      const params = new URLSearchParams()
      params.append('id', form.id)
      params.append('shop_id', form.shop_id)
      params.append('name', form.name)
      params.append('sku_number', form.sku_number)
      params.append('price', form.price)
      params.append('unit', form.unit)
      params.append('weight', form.weight)
      params.append('stock_original', form.stock_original)
      params.append('stock', form.stock)
      params.append('purchase_channel', form.purchase_channel)
      params.append('upFiles', JSON.stringify((form.imageFiles || []).filter(file => file.isNew).map(file => file.storedName)))
      params.append('oldFiles', JSON.stringify((form.imageFiles || []).filter(file => !file.isNew).map(file => file.storedName || file.name)))
      const res = await api.post('saveSku', params, 'Admin')
      if (res.data.status === 1) {
        await dispatch('fetchSkuList')// 刷新商品列表
        Message.success(t(res.data.msg))
      } else {
        Message.error(t(res.data.msg))
      }
      return res
    },
    /**
     * @description: 删除sku
     * @param {*} 多选id组
     */
    async fetchDelSku ({ dispatch }, idArr) {
      if (idArr.length === 0) {
        Message.error(t('未勾选记录'))
      } else {
        MessageBox.confirm(t('请谨慎操作 确认删除?'), t('提示'), {
          confirmButtonText: t('确定'),
          cancelButtonText: t('取消'),
          type: 'warning'
        }).then(() => {
          const params = new URLSearchParams()
          params.append('idArr', idArr)
          api.post('deleteSku', params, 'Admin').then(res => {
            if (res.data.status === 1) {
              Message.success(t(res.data.msg))
              dispatch('fetchSkuList')// 刷新列表
            } else {
              Message.error(t(res.data.msg))
            }
          })
        }).catch(() => {
          Message.info(t('取消操作'))
        })
      }
    },
    /**
     * @description: 获取已付款 和已退款但是发货状态为 已发货 订单列表
     * @return {*} 列表
     */
    async fetchPaidOrderList ({ commit, state }) {
      const isLatest = beginTaskRefresh('fetchPaidOrderList', state)
      const res = await api.get('getPaidOrderList', 'Admin')
      if (!res.data || !Array.isArray(res.data.paidOrderList) || ![0, 1].includes(Number(res.data.status)) || (Number(res.data.status) === 0 && res.data.paidOrderList.length > 0)) {
        throw new Error((res.data && res.data.msg) || '数据读取失败')
      }
      if (!isLatest()) return res
      if (res.data.status === 1) {
        const today = new Date(Date.now() + 8 * 60 * 60 * 1000).toISOString().slice(0, 10)
        const dayStart = Date.parse(today + 'T00:00:00+08:00') / 1000
        const dayEnd = dayStart + 24 * 60 * 60
        const list = res.data.paidOrderList.filter(item => Number(item.order_time) >= dayStart && Number(item.order_time) < dayEnd)
        list.forEach((item, index) => { // 拿到订单列表数据 直接把订单列表数据反序列化
          list[index].product_snapshot = parseJsonArray(item.product_snapshot)
        })
        commit('setPaidOrderList', list)
      } else {
        commit('setPaidOrderList', [])
      }
      return res
    },

    /**
     * @description: 异步获取管理员消息列表，成功获取之后弹出通知框显示列表
     */
    async fetchMessageList ({ commit, state, rootGetters, rootState, isDataSyncCurrent = () => true }) {
      const res = await api.get('getMessageList', 'Admin')
      if (!isDataSyncCurrent()) return res
      if (!res.data || Number(res.data.status) !== 1 || !Array.isArray(res.data.messageList)) throw new Error((res.data && res.data.msg) || '消息读取失败')
      const previous = new Map(state.messageList.map(item => [String(item.id), JSON.stringify(item)]))

      if (res.data.status === 1) {
        const list = res.data.messageList || []
        commit('setMessageList', list)

        const now = Math.floor(Date.now() / 1000)
        const unreadList = list.filter(item => {
          const messageId = String(item.id || '').trim()
          const endTime = Number(item.end_time || 0)
          return messageId && previous.get(messageId) !== JSON.stringify(item) && endTime > now && !rootGetters['app/isMessageRead'](messageId)
        })

        unreadList.forEach((item, index) => {
          const messageId = String(item.id || '').trim()
          const noticeDate = getOrderArchiveNoticeDate(item, rootState.user)
          const formattedTime = parseTime(item.add_time, '{m}-{d} {h}:{i}')
          const sender = String(item.by_admin_id) === '0'
            ? '系统'
            : (item.by_admin_uname || item.by_admin_name || '管理员')

          setTimeout(() => {
            if (!isDataSyncCurrent() || rootGetters['app/isMessageRead'](messageId)) return
            openAnnouncementNotification({
              title: item.tit || '公告通知',
              message: item.message,
              sender,
              time: formattedTime,
              onRead: () => store.commit('app/markMessageRead', messageId),
              onAction: noticeDate
                ? () => import('@/router').then(({ default: router }) => router.push({ path: '/order/archive', query: { date: noticeDate } }).catch(() => {}))
                : null
            })
          }, index * 800)
        })
      } else {
        Message.error(t(res.data.msg || '获取消息失败'))
        commit('setMessageList', [])
      }

      return res
    },
    /**
     * @description: 向操作日志插入信息
     * @param {Object} logData 日志对象
     * @param {String} logData.planeName 飞机名称
     * @param {String} logData.content 日志内容
     * @param {String} logData.color 标点颜色，默认读取当前主题
     * @param {Number} logData.timestamp 插入时间 默认当前时间
     */
    fetchLog ({ commit }, logData) {
      /* 空日志不插入 */
      if (typeof logData.content === 'undefined' || logData.content === null || String(logData.content).trim() === '') {
        return
      }

      /* 插入日志 */
      const rawSourceType = String(logData.sourceType || logData.source_type || '').trim().toLowerCase()
      const sourceTypeMap = {
        fc: 'fc',
        飞控: 'fc',
        esp: 'esp',
        esp32: 'esp',
        机载端: 'esp',
        terminal: 'terminal',
        终端: 'terminal',
        终端操作: 'terminal',
        system: 'system',
        系统: 'system'
      }
      const colorSourceType = String(logData.color || '').toLowerCase() === getEventThemeColor('system').toLowerCase()
        ? 'system'
        : ''
      logData.sourceType = sourceTypeMap[rawSourceType] || colorSourceType || 'esp'
      logData.planeName = typeof logData.planeName !== 'undefined' ? logData.planeName : ''
      logData.content = String(logData.content).trim()
      logData.color = typeof logData.color !== 'undefined' ? logData.color : getEventThemeColor(logData.sourceType)
      logData.timestamp = typeof logData.timestamp !== 'undefined' ? logData.timestamp : new Date().getTime()
      commit('insertNewLog', logData)
    },
    /**
   * @description: 获取地图样式列表
   * @param {*} param0.rootState 传this.$store.state.settings.host 即云端存放sprite 的地址
   */
    async fetchMapStyleList ({ commit, rootState }) {
      const prefix = store.state.user.is_god === true ? 'God' : 'Plane'
      const res = await api.get('getMapStyleList', prefix)
      if (!res.data || !Array.isArray(res.data.mapStyleList) || ![0, 1].includes(Number(res.data.status)) || (Number(res.data.status) === 0 && res.data.mapStyleList.length > 0)) {
        throw new Error((res.data && res.data.msg) || '数据读取失败')
      }
      if (res.data.status === 1) {
        const host = rootState.settings.host
        const list = res.data.mapStyleList.map(style => {
          const styleUrl = style.url ? String(style.url).trim() : ''
          const mapboxStyleMatch = styleUrl.match(/^mapbox:\/\/styles\/([^/]+)\/([^/?]+)$/)
          const mapboxHttpStyleMatch = styleUrl.match(/^https:\/\/api\.mapbox\.com\/styles\/v1\/([^/]+)\/([^/?]+)(?:\?.*)?$/)
          const vectorStyleUrl = mapboxStyleMatch
            ? styleUrl
            : (mapboxHttpStyleMatch
              ? `mapbox://styles/${mapboxHttpStyleMatch[1]}/${mapboxHttpStyleMatch[2]}`
              : '')
          const tileUrl = styleUrl
          const demUrl = style.dem_url ? String(style.dem_url).trim() : ''
          const demEncoding = String(style.dem_encoding || 'terrarium').toLowerCase() === 'mapbox'
            ? 'mapbox'
            : 'terrarium'

          // Mapbox Style URL 直接交给 Mapbox GL 加载，以保留道路、文字等矢量图层。
          if (vectorStyleUrl) {
            return {
              id: style.id,
              is_active: Number(style.is_active ?? 1),
              god_only: Number(style.god_only ?? 0),
              sort_order: Number(style.sort_order ?? 0),
              name: style.name,
              dem_url: demUrl,
              dem_encoding: demEncoding,
              styleUrl: vectorStyleUrl,
              _original: {
                url: style.url,
                sprite: style.sprite,
                glyphs: style.glyphs,
                dem_url: demUrl,
                dem_encoding: demEncoding
              }
            }
          }

          // 使用 url 字段，并包装成 raster 瓦片数组
          const tiles = tileUrl ? [tileUrl] : (Array.isArray(style.tiles) ? style.tiles : [])
          const sourceKey = 'default'
          const configuredSprite = style.sprite && String(style.sprite).trim() ? style.sprite : `${host}/Public/map/sprite`
          const spriteUrl = process.env.NODE_ENV === 'development' && configuredSprite === `${host}/Public/map/sprite`
            ? new URL('/Public/map/sprite', window.location.origin).href
            : new URL(configuredSprite, host).href
          const glyphs = style.glyphs || 'mapbox://fonts/mapbox/{fontstack}/{range}.pbf'

          return {
            // 附带原始字段，便于管理端展示和操作
            id: style.id,
            is_active: Number(style.is_active ?? 1),
            god_only: Number(style.god_only ?? 0),
            sort_order: Number(style.sort_order ?? 0),
            name: style.name,
            dem_url: demUrl,
            dem_encoding: demEncoding,
            sprite: spriteUrl,
            glyphs: glyphs,
            version: 8,
            sources: {
              [sourceKey]: {
                type: 'raster',
                tileSize: 256,
                tiles: tiles,
                attribution: ''
              }
            },
            layers: [{
              id: `${sourceKey}Layer`,
              type: 'raster',
              source: sourceKey
            }],
            // 保存原始URL用于编辑
            _original: {
              url: style.url || (tiles.length > 0 ? tiles[0] : ''),
              sprite: style.sprite,
              glyphs: style.glyphs,
              dem_url: demUrl,
              dem_encoding: demEncoding
            }
          }
        })
        commit('setMapStyleList', list)
      } else {
        commit('setMapStyleList', [])
      }
    },
    /**
     * @description: 地图样式排序
     * @param {*} form { id, sort_order }
     */
    async fetchOrderMapStyle ({ dispatch }, form) {
      const params = new URLSearchParams()
      params.append('id', form.id)
      params.append('sort_order', form.sort_order)
      const res = await api.post('orderMapStyle', params, 'God')
      if (res.data.status === 1) {
        await dispatch('fetchMapStyleList') // 刷新样式列表
        Message.success(t(res.data.msg || '排序已更新'))
      } else {
        Message.error(t(res.data.msg || '排序更新失败'))
      }
      return res
    },

    /**
     * @description: 删除地图样式
     * @param {Array} idArr 需要删除的样式ID数组
     */
    async fetchDelMapStyle ({ dispatch }, idArr) {
      if (!idArr || !idArr.length) {
        Message.warning(t('请选择要删除的样式'))
        return Promise.reject(new Error('未选择要删除的样式'))
      }

      try {
        await MessageBox.confirm(
          t(`确定要删除选中的 ${idArr.length} 个地图样式吗？删除后不可恢复！`),
          t('提示'),
          {
            confirmButtonText: t('确定'),
            cancelButtonText: t('取消'),
            type: 'warning',
            closeOnClickModal: false
          }
        )

        const params = new URLSearchParams()
        params.append('id', idArr.join(','))
        const res = await api.post('delMapStyle', params, 'God')

        if (res.data.status === 1) {
          Message.success(t(res.data.msg || '删除成功'))
          // 重新获取样式列表
          await dispatch('fetchMapStyleList')
        } else {
          Message.error(t(res.data.msg || '删除失败'))
        }
        return res
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除样式失败:', error)
          Message.error(t('删除失败'))
          throw error
        }
        return Promise.reject(new Error('用户取消删除'))
      }
    },
    /**
     * @description: 添加地图样式
     * @param {Object} form 地图样式表单数据
     * @param {string} form.name 样式名称
     * @param {string} form.url 样式URL
     * @param {string} form.thumbnail 缩略图URL
     * @param {number} form.sort_order 排序值
     * @return {Promise} 返回包含服务器响应的Promise
     */
    async addMapStyle ({ dispatch }, form) {
      // 参数验证
      if (!form || typeof form !== 'object') {
        throw new Error('无效的表单数据')
      }

      if (!form.name || !form.name.trim()) {
        throw new Error('样式名称不能为空')
      }

      if (!form.url || !form.url.trim()) {
        throw new Error('样式URL不能为空')
      }

      // 处理form参数为URLSearchParams格式
      const params = new URLSearchParams()
      params.append('name', form.name)
      params.append('sprite', form.sprite || '')
      params.append('glyphs', form.glyphs || 'mapbox://fonts/mapbox/{fontstack}/{range}.pbf')
      params.append('url', form.url)
      params.append('dem_url', form.dem_url || '')
      params.append('dem_encoding', form.dem_encoding || 'terrarium')
      params.append('is_active', form.is_active ? 1 : 0)
      params.append('god_only', form.god_only ? 1 : 0)
      params.append('sort_order', form.sort_order || 0)

      // 发送请求
      const res = await api.post('addMapStyle', params, 'God')

      // 检查响应状态
      if (res.data.status === 1) {
        await dispatch('fetchMapStyleList') // 刷新列表
        Message.success(t(res.data.msg))
      } else {
        Message.error(t(res.data.msg))
      }
      return res
    },

    async saveMapStyle ({ dispatch }, form) {
      // 参数验证
      if (!form || typeof form !== 'object') {
        throw new Error('无效的表单数据')
      }

      if (!form.id) {
        throw new Error('缺少样式ID')
      }

      // 处理form参数为URLSearchParams格式
      const params = new URLSearchParams()
      params.append('id', form.id)

      if (form.name !== undefined) params.append('name', form.name)
      if (form.sprite !== undefined) params.append('sprite', form.sprite)
      if (form.glyphs !== undefined) params.append('glyphs', form.glyphs)
      if (form.url !== undefined) params.append('url', form.url)
      if (form.dem_url !== undefined) params.append('dem_url', form.dem_url)
      if (form.dem_encoding !== undefined) params.append('dem_encoding', form.dem_encoding)
      if (form.is_active !== undefined) params.append('is_active', form.is_active ? 1 : 0)
      if (form.god_only !== undefined) params.append('god_only', form.god_only ? 1 : 0)
      if (form.sort_order !== undefined) params.append('sort_order', form.sort_order)

      // 发送请求
      const res = await api.post('saveMapStyle', params, 'God')

      // 检查响应状态
      if (res.data.status === 1) {
        await dispatch('fetchMapStyleList') // 刷新列表
        Message.success(t(res.data.msg))
      } else {
        Message.error(t(res.data.msg))
      }
      return res
    }
  },
  modules: {
    app,
    settings,
    user
  },
  getters: {
  }
}))

export default store
